import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DomainsSettingsTab from '../settings/DomainsSettingsTab';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';
import { PublishBannerView } from '../../components/ui/PublishBanner';
import PublishButton from '../../components/ui/PublishButton';
import { usePublish } from '../../lib/usePublish';
import { PagesTabContent } from '../pages/PagesPage';
import LegalTabContent from './LegalTabContent';
import LinksTabContent from './LinksTabContent';
import AnnouncementsTabContent from './AnnouncementsTabContent';
import SiteAppearanceTab from './SiteAppearanceTab';
import SiteContactTab from './SiteContactTab';
import SiteLanguagesTab from './SiteLanguagesTab';
import SiteTabsNav from './SiteTabsNav';
import { SITE_TAB_CONTEXT, SITE_TAB_ITEMS, type SiteTab } from './sitePageTypes';
import { useSiteSettings } from './useSiteSettings';

export default function SitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<SiteTab>((searchParams.get('tab') as SiteTab) ?? 'paginas');
  const { last: lastDeploy, phase: publishPhase, error: publishError, publish } = usePublish();
  const {
    settings,
    saving,
    needsPublish,
    isSaasAdmin,
    industry,
    theme,
    navLogoUrl,
    footerLogoUrl,
    footerLogoMobileUrl,
    navLogoShowName,
    footerLogoShowName,
    faviconUrl,
    contact,
    social,
    hasOfficesType,
    setTheme,
    setNavLogoUrl,
    setFooterLogoUrl,
    setFooterLogoMobileUrl,
    setNavLogoShowName,
    setFooterLogoShowName,
    setFaviconUrl,
    setContact,
    setSocial,
    setNeedsPublish,
    save,
    saveTheme,
  } = useSiteSettings();

  return (
    <PageContainer>
      <PageHeader title="Mi Sitio">
        {lastDeploy && (
          <span className="flex items-center gap-2 text-xs text-ui-text-subtle">
            <span className={`w-1.5 h-1.5 rounded-full ${
              lastDeploy.status === 'success' ? 'bg-ui-success' :
              lastDeploy.status === 'error' ? 'bg-ui-danger' : 'bg-ui-warning'
            }`} />
            Última publicación: <span className="capitalize">{lastDeploy.status === 'success' ? 'exitosa' : lastDeploy.status === 'error' ? 'con error' : 'en proceso'}</span>
          </span>
        )}
        <PublishButton label="Publicar sitio" onPublish={publish} />
      </PageHeader>

      <SiteTabsNav items={SITE_TAB_ITEMS} tab={tab} onTabChange={setTab} />

      <ContextCard {...SITE_TAB_CONTEXT[tab]} />

      {tab !== 'paginas' && (
        <PublishBannerView
          needsPublish={needsPublish}
          phase={publishPhase}
          error={publishError}
          publish={publish}
          onPublished={() => setNeedsPublish(false)}
        />
      )}

      <div className="flex-1 min-w-0">
        {tab === 'paginas' && <PagesTabContent showPublish={false} />}
        {tab === 'apariencia' && (
          <SiteAppearanceTab
            industry={industry}
            theme={theme}
            navLogoUrl={navLogoUrl}
            footerLogoUrl={footerLogoUrl}
            footerLogoMobileUrl={footerLogoMobileUrl}
            navLogoShowName={navLogoShowName}
            footerLogoShowName={footerLogoShowName}
            faviconUrl={faviconUrl}
            saving={saving}
            setTheme={setTheme}
            setNavLogoUrl={setNavLogoUrl}
            setFooterLogoUrl={setFooterLogoUrl}
            setFooterLogoMobileUrl={setFooterLogoMobileUrl}
            setNavLogoShowName={setNavLogoShowName}
            setFooterLogoShowName={setFooterLogoShowName}
            setFaviconUrl={setFaviconUrl}
            onSave={saveTheme}
          />
        )}
        {tab === 'contacto' && (
          <SiteContactTab
            contact={contact}
            social={social}
            hasOfficesType={hasOfficesType}
            saving={saving}
            settingsReady={Boolean(settings)}
            onManageOffices={() => navigate('/c/offices')}
            setContact={setContact}
            setSocial={setSocial}
            onSave={save}
          />
        )}

        {tab === 'idiomas' && <SiteLanguagesTab onSaved={() => setNeedsPublish(true)} />}
        {tab === 'enlaces' && <LinksTabContent onSaved={() => setNeedsPublish(true)} />}
        {tab === 'anuncios' && <AnnouncementsTabContent onSaved={() => setNeedsPublish(true)} />}
        {tab === 'legal' && <LegalTabContent onSaved={() => setNeedsPublish(true)} />}
        {tab === 'dominio' && <DomainsSettingsTab isSaasAdmin={isSaasAdmin} />}
      </div>
    </PageContainer>
  );
}
